// PHA-567 citation chip — clicking dispatches `phanova:open-patient-detail`
// with the numeric `initialTab` per citation kind, reusing the same custom
// event AckDrawer/TaskBar already use (AckDrawer.tsx imports the same
// PATIENT_DETAIL_TAB_INDEX). We do NOT add a parallel deep-link mechanism.

import React from 'react';
import { Badge, Tooltip } from '@radix-ui/themes';
// Import from the standalone tab-index module to avoid the circular
// PatientDetail → AiInsightChartTab → CitationLink → PatientDetail cycle.
import { PATIENT_DETAIL_TAB_INDEX } from '../../PatientDetailTabIndex';
import type { AiInsightCitation, AiInsightCitationKind } from '../types';

interface CitationLinkProps {
  citation: AiInsightCitation;
  patientId: string;
}

// Map citation kinds → numeric tab indices, mirroring AckDrawer's pattern.
// PHA-1141/1142 alignment: an `ehr_change` citation names the item's
// change_kind in its ref_id (problem_* | med_*), and the review panel for
// those items now lives in the Problems / Medications tabs — not Care
// Plans. Unrecognized refs keep the legacy Care Plans fallback.
const tabForEhrChangeRefId = (refId: string): number => {
  if (refId.startsWith('problem_')) return PATIENT_DETAIL_TAB_INDEX.PROBLEMS;
  if (refId.startsWith('med_')) return PATIENT_DETAIL_TAB_INDEX.MEDICATIONS;
  return PATIENT_DETAIL_TAB_INDEX.CARE_PLANS;
};

const tabForKind = (citation: AiInsightCitation): number =>
  citation.kind === 'ehr_change'
    ? tabForEhrChangeRefId(citation.ref_id)
    : {
        reading: PATIENT_DETAIL_TAB_INDEX.DEVICE_READINGS,
        survey: PATIENT_DETAIL_TAB_INDEX.ANSWERS,
        ehr_change: PATIENT_DETAIL_TAB_INDEX.CARE_PLANS,
        call: PATIENT_DETAIL_TAB_INDEX.PATIENT_OUTREACH,
        task: PATIENT_DETAIL_TAB_INDEX.CARE_PLANS,
        lab: PATIENT_DETAIL_TAB_INDEX.PROBLEMS,
      }[citation.kind];

const labelForKind: Record<AiInsightCitationKind, string> = {
  reading: 'Reading',
  survey: 'Answer',
  ehr_change: 'EHR change',
  call: 'Call',
  task: 'Task',
  lab: 'Lab',
};

export const CitationLink: React.FC<CitationLinkProps> = ({ citation, patientId }) => {
  const handleOpen = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('phanova:open-patient-detail', {
        detail: {
          patientId,
          initialTab: tabForKind(citation),
          source: 'smart_hub_ai_citation',
          ref_id: citation.ref_id,
        },
      }),
    );
  };
  return (
    <Tooltip content={citation.snippet}>
      <Badge
        color="gray"
        variant="soft"
        size="1"
        onClick={handleOpen}
        style={{ cursor: 'pointer' }}
        title={citation.snippet}
      >
        {labelForKind[citation.kind]}: {citation.snippet}
      </Badge>
    </Tooltip>
  );
};

export default CitationLink;
